/**
 * LazyImage - Image component that only loads when visible in viewport
 * Uses IntersectionObserver for true lazy loading in scrollable containers
 */

import { useState, useRef, useEffect, ImgHTMLAttributes } from "react";

interface LazyImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  /** Placeholder shown before image loads */
  placeholder?: React.ReactNode;
  /** Root margin for intersection observer (load before entering view) */
  rootMargin?: string;
}

export function LazyImage({
  src,
  alt,
  className,
  placeholder,
  rootMargin = "100px",
  ...props
}: LazyImageProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div ref={imgRef} className={className}>
      {isVisible ? (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onLoad={() => setIsLoaded(true)}
          style={{ opacity: isLoaded ? 1 : 0, transition: "opacity 0.2s" }}
          {...props}
        />
      ) : (
        (placeholder ?? <div className="w-full h-full bg-white/5" />)
      )}
    </div>
  );
}
